import React, { ReactNode, createContext, useContext } from "react";
import {
  AudioTrack,
  LiveKitRoom,
  useLocalParticipant,
  useTracks,
} from "@livekit/components-react";
import { MediaDeviceFailure, Track } from "livekit-client";
import { captureError } from "@/utils/logger";
import { useGlobalState } from "@/hooks";

interface LiveKitContextType {}

const LiveKitContext = createContext<LiveKitContextType | undefined>(undefined);

export const useLiveKit = () => {
  const context = useContext(LiveKitContext);
  if (!context) {
    throw new Error("useLiveKit must be used within a LiveKitProvider");
  }
  return context;
};

interface LiveKitProviderProps {
  children: ReactNode;
}

const LiveKitProvider: React.FC<LiveKitProviderProps> = ({ children }) => {
  const {
    connectionDetails,
    setConnectionDetails,
    setShowReconnectPrompt,
  } = useGlobalState();
  const contextValue = {};

  return (
    <LiveKitContext.Provider value={contextValue}>
      <LiveKitRoom
        token={connectionDetails?.participantToken}
        serverUrl={connectionDetails?.serverUrl}
        connect={connectionDetails !== undefined}
        // Since we have text mode we don't always need audio
        audio={false}
        onMediaDeviceFailure={onDeviceFailure}
        onDisconnected={() => {
          setConnectionDetails(undefined);
          setShowReconnectPrompt(true);
        }}
        className={"h-full"}
      >
        {children}
      </LiveKitRoom>
    </LiveKitContext.Provider>
  );
};

export default LiveKitProvider;

export function CustomAudioRenderer() {
  const { localParticipant } = useLocalParticipant();
  const tracks = useTracks([
    Track.Source.Microphone,
    Track.Source.ScreenShareAudio,
    Track.Source.Unknown,
  ]).filter(
    (itemTrackRef) =>
      itemTrackRef.participant !== localParticipant &&
      itemTrackRef.publication.kind === Track.Kind.Audio,
  );

  return (
    <div key="audio-renderer" style={{ display: "none" }}>
      {tracks.map((trackRef) => (
        <AudioTrack
          key={trackRef.participant.identity}
          trackRef={trackRef}
          volume={1}
          muted={false}
        />
      ))}
    </div>
  );
}

function onDeviceFailure(error?: MediaDeviceFailure) {
  captureError(
    "Error acquiring camera or microphone permissions. Please make sure you grant the necessary permissions in your browser and reload the tab",
    error,
  );
}
